<template>
  <main>
    <!-- Template Header -->
    <page-header-compact>
        Monitoreo de Envíos IHCE (RDA)
    </page-header-compact>


    <!-- Template Content Container -->
    <div class="container-fluid px-4 mt-2">
      <div class="card shadow-none border-gray-300 mb-4">
        <!-- Tabs matching template layout -->
        <div class="card-header bg-light">
          <ul class="nav nav-tabs card-header-tabs" id="logsTab" role="tablist">
            <li class="nav-item">
              <a 
                class="nav-link" 
                :class="{ active: activeTab === 'success' }" 
                @click.prevent="switchTab('success')"
                href="#"
              >
                <i class="fas fa-check-circle mr-2 text-success"></i>Enviados con Éxito
                <span class="badge badge-pill badge-success-soft ml-1 px-2">{{ counts.success }}</span>
              </a>
            </li>
            <li class="nav-item">
              <a 
                class="nav-link" 
                :class="{ active: activeTab === 'failed' }" 
                @click.prevent="switchTab('failed')"
                href="#"
              >
                <i class="fas fa-exclamation-triangle mr-2 text-danger"></i>Envíos Fallidos (Errores)
                <span class="badge badge-pill badge-danger-soft ml-1 px-2">{{ counts.failed }}</span>
              </a>
            </li>
          </ul>
        </div>

        <!-- Filter Bar using template solid elements -->
        <div class="border-bottom py-3 px-4">
          <div class="form-row align-items-center">
            <!-- Search input -->
            <div class="col-lg-3 col-md-6 mb-2 mb-lg-0">
              <input 
                type="text" 
                class="form-control form-control-sm" 
                v-model="filters.search" 
                placeholder="Buscar por HC, paciente o documento..."
                @input="onSearchInput"
              />
            </div>

            <!-- Type RDA Filter -->
            <div class="col-lg-3 col-md-6 mb-2 mb-lg-0">
              <select 
                class="form-control form-control-sm " 
                v-model="filters.type_rda" 
                @change="fetchLogs"
              >
                <option value="">Todos los Tipos RDA</option>
                <option v-for="type in rdaTypes" :key="type" :value="type">{{ type }}</option>
              </select>
            </div>

            <!-- Date Range Filters -->
            <div class="col-lg-4 col-md-8 mb-2 mb-lg-0 d-flex align-items-center">
              <span class="mr-2 text-muted small font-weight-bold">Desde</span>
              <input 
                type="date" 
                class="form-control form-control-sm mr-3" 
                v-model="filters.date_start" 
                @change="fetchLogs"
              />
              <span class="mr-2 text-muted small font-weight-bold">Hasta</span>
              <input 
                type="date" 
                class="form-control form-control-sm" 
                v-model="filters.date_end" 
                @change="fetchLogs"
              />
            </div>

            <!-- Reset Button -->
            <div class="col-lg-2 col-md-4 mb-2 mb-lg-0 text-right pl-lg-4">
              <button 
                class="btn btn-sm btn-light  font-weight-bold" 
                :disabled="!hasActiveFilters" 
                @click="clearAllFilters"
                title="Limpiar Filtros"
              >
                <i class="fas fa-undo mr-1"></i> Limpiar
              </button>
            </div>
          </div>
        </div>

        <!-- Table Grid -->
        <div class="card-body p-0">
          <div class="table-responsive">
            <table class="table table-sm mb-0 align-middle">
              <thead class="bg-gray-100">
                <tr class="text-gray-700 font-weight-bold">
                  <th class="px-4 py-3"># Log</th>
                  <th class="py-3">Fecha de Envío</th>
                  <th class="py-3">RDA / Historia Clínica</th>
                  <th class="py-3">Paciente</th>
                  <th class="py-3 text-center">Intentos</th>
                  <th class="px-4 py-3 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="loading">
                  <td colspan="6" class="text-center py-5">
                    <div class="spinner-border text-primary" role="status">
                      <span class="sr-only">Cargando...</span>
                    </div>
                    <p class="text-muted small mt-2 mb-0">Obteniendo registros...</p>
                  </td>
                </tr>
                <tr v-else-if="logs.length === 0">
                  <td colspan="6" class="text-center py-5 text-muted">
                    <i class="fas fa-folder-open fa-2x mb-3 text-muted"></i>
                    <p class="mb-0">No se encontraron registros de envío con este estado.</p>
                  </td>
                </tr>
                <tr v-else v-for="log in logs" :key="log.id">
                  <td class="px-4 font-weight-bold text-dark">#{{ log.id }}</td>
                  <td>{{ formatDate(log.updated_at) }}</td>
                  <td>
                    <div class="d-flex align-items-center mb-1">
                      <span class="font-weight-bold text-dark mr-2" style="font-size: 0.9rem;">
                        HC-{{ log.id_historia_clinica }}
                      </span>
                      <span class="badge badge-light-blue px-2 py-0.5 font-weight-bold text-uppercase" style="font-size: 0.65rem; border-radius: 4px;">
                        {{ log.type_rda }}
                      </span>
                    </div>
                    <div class="small text-muted font-weight-500" v-if="log.historia_clinica && log.historia_clinica.tipo_historia">
                      {{ log.historia_clinica.tipo_historia.nombre }}
                    </div>
                  </td>
                  <td>
                    <div class="font-weight-bold text-dark" v-if="log.historia_clinica && log.historia_clinica.usuario">
                      {{ formatPacienteName(log.historia_clinica.usuario) }}
                    </div>
                    <div class="small text-muted" v-if="log.historia_clinica && log.historia_clinica.usuario">
                      {{ log.historia_clinica.usuario.documento }}
                    </div>
                  </td>
                  <td class="text-center">
                    <span class="badge badge-pill font-weight-bold py-1.5 px-2.5" :class="getAttemptsBadgeClass(log.attempts)">
                      <i v-if="log.attempts > 5" class="fas fa-exclamation-triangle mr-1 text-danger animate-pulse"></i>
                      <i v-else-if="log.attempts > 3" class="fas fa-exclamation-circle mr-1 text-warning"></i>
                      {{ log.attempts }} / 3
                    </span>
                  </td>
                  <td class="px-4 text-right">
                    <div class="btn-group btn-group-sm" role="group">
                      <button 
                        class="btn btn-sm btn-transparent text-dark shadow-sm mr-2" 
                        @click="showDetails(log)"
                        title="Ver Detalles de Intentos"
                      >
                        <i class="fas fa-eye text-dark mr-1"></i>Ver Detalle
                      </button>
                      <button 
                        v-if="log.status === 'failed'"
                        class="btn btn-sm btn-primary shadow-sm" 
                        @click="retryLog(log)"
                        :disabled="retryingId === log.id"
                        title="Reintentar Envío"
                      >
                        <i class="fas fa-sync-alt mr-1" :class="{ 'fa-spin': retryingId === log.id }"></i>Reintentar
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Pagination Footer -->
        <div class="card-footer bg-white border-top py-3 d-flex align-items-center justify-content-between" v-if="total_pages > 1">
          <div class="small text-muted">
            Mostrando página {{ pagination.current_page }} de {{ total_pages }} (Total: {{ pagination.total }} registros)
          </div>
          <nav aria-label="Page navigation">
            <ul class="pagination pagination-sm mb-0">
              <li class="page-item" :class="{ disabled: pagination.current_page <= 1 }">
                <a class="page-link" href="#" @click.prevent="prevPage()">&laquo; Anterior</a>
              </li>
              <li 
                v-for="page in pages" 
                :key="page" 
                class="page-item" 
                :class="{ active: pagination.current_page === page }"
              >
                <a class="page-link" href="#" @click.prevent="changePage(page)">{{ page }}</a>
              </li>
              <li class="page-item" :class="{ disabled: pagination.current_page >= total_pages }">
                <a class="page-link" href="#" @click.prevent="nextPage()">Siguiente &raquo;</a>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>

    <!-- Details Modal -->
    <div class="modal fade show" v-if="selectedLog" style="display: block; background: rgba(0, 0, 0, 0.5);" tabindex="-1" role="dialog">
      <div class="modal-dialog modal-lg modal-dialog-centered" role="document">
        <div class="modal-content border-0 shadow-lg">
          <div class="modal-header bg-light py-3">
            <h5 class="modal-title font-weight-bold text-dark">
              <i class="fas fa-list-ul mr-2 text-primary"></i>Historial de Intentos - Log #{{ selectedLog.id }}
            </h5>
            <button type="button" class="close" @click="closeModal" aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body p-4" style="max-height: 70vh; overflow-y: auto;">
            <div class="row mb-3">
              <div class="col-md-6">
                <p class="mb-1 text-muted small">Paciente</p>
                <h6 class="font-weight-bold" v-if="selectedLog.historia_clinica && selectedLog.historia_clinica.usuario">
                  {{ formatPacienteName(selectedLog.historia_clinica.usuario) }} ({{ selectedLog.historia_clinica.usuario.documento }})
                </h6>
              </div>
              <div class="col-md-6">
                <p class="mb-1 text-muted small">Historia Clínica</p>
                <h6 class="font-weight-bold">ID HC-{{ selectedLog.id_historia_clinica }}</h6>
              </div>
            </div>

            <hr>

            <h6 class="font-weight-bold text-secondary mb-3">Intentos Registrados</h6>

            <div v-if="loadingDetails" class="text-center py-4">
              <div class="spinner-border spinner-border-sm text-secondary" role="status"></div>
              <p class="small text-muted mt-2">Cargando detalles...</p>
            </div>
            
            <div v-else-if="!logDetails || logDetails.length === 0" class="text-center py-3 text-muted">
              No hay detalles registrados para este log.
            </div>

            <div v-else v-for="detalle in logDetails" :key="detalle.id" class="card border-light mb-3">
              <div class="card-header bg-transparent d-flex justify-content-between align-items-center py-2">
                <span class="font-weight-bold text-primary small">Intento #{{ detalle.attempt }}</span>
                <div>
                  <span class="badge" :class="detalle.status === 'success' ? 'badge-success' : 'badge-danger'">
                    {{ detalle.status === 'success' ? 'Exitoso' : 'Fallido' }}
                  </span>
                  <span class="badge badge-light ml-1" v-if="detalle.http_code">
                    HTTP {{ detalle.http_code }}
                  </span>
                </div>
              </div>
              <div class="card-body bg-light py-2 px-3">
                <p class="mb-1 small text-muted">Fecha: {{ formatDate(detalle.created_at) }}</p>
                <div v-if="detalle.response" class="response-container">
                  <p class="mb-1 small font-weight-bold text-secondary">Respuesta / Error:</p>
                  <pre class="small bg-white p-2 border rounded text-dark text-wrap">{{ formatJSON(detalle.response) }}</pre>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer bg-light py-2">
            <button type="button" class="btn btn-secondary btn-sm" @click="closeModal">Cerrar</button>
            <button 
              v-if="selectedLog.status === 'failed'"
              type="button" 
              class="btn btn-primary btn-sm" 
              @click="retryLogFromModal"
              :disabled="retryingId === selectedLog.id"
            >
              <i class="fas fa-sync-alt mr-1" :class="{ 'fa-spin': retryingId === selectedLog.id }"></i>Reintentar
            </button>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import ihceService from "@/services/ihceService";
import Toast from "@/components/common/utilities/toast";
import dayjs from "dayjs";
import PageHeaderCompact from "../../../components/layouts/content/page-header-compact";

export default {
  components: {
    PageHeaderCompact},
  data() {
    return {
      activeTab: "failed", // Iniciamos por defecto en fallidos/errores para gestión directa
      logs: [],
      loading: false,
      retryingId: null,
      selectedLog: null,
      logDetails: [],
      loadingDetails: false,
      pagination: {
        page: 1,
        per_page: 15,
        total: 0
      },
      filters: {
        search: "",
        type_rda: "",
        date_start: "",
        date_end: ""
      },
      counts: {
        success: 0,
        failed: 0
      },
      rdaTypes: [],
      searchTimeout: null
    };
  },
  computed: {
    total_pages() {
      return this.pagination.total ? Math.ceil(this.pagination.total / this.pagination.per_page) : 0;
    },
    pages() {
      return Array(this.total_pages)
        .fill(1)
        .map((value, index) => Number(value) + index)
        .filter(
          (value) =>
            value >= this.pagination.page - 3 &&
            value <= Number(this.pagination.page) + 3
        );
    },
    hasActiveFilters() {
      return !!(
        this.filters.search ||
        this.filters.type_rda ||
        this.filters.date_start ||
        this.filters.date_end
      );
    }
  },
  methods: {
    async fetchLogs() {
      this.loading = true;
      try {
        const response = await ihceService.getLogs({
          page: this.pagination.page,
          status: this.activeTab,
          search: this.filters.search,
          type_rda: this.filters.type_rda,
          date_start: this.filters.date_start,
          date_end: this.filters.date_end
        });
        this.logs = response.data.data;
        this.pagination.total = response.data.total;
        this.pagination.page = response.data.current_page;

        // Dynamic counts
        if (response.data.counts) {
          this.counts.success = response.data.counts.success || 0;
          this.counts.failed = response.data.counts.failed || 0;
        }

        // Dynamic types
        if (response.data.types) {
          this.rdaTypes = response.data.types || [];
        }
      } catch (e) {
        console.error(e);
        Toast.fire({
          icon: "error",
          title: "Error al cargar los logs de IHCE."
        });
      } finally {
        this.loading = false;
      }
    },
    onSearchInput() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.pagination.page = 1;
        this.fetchLogs();
      }, 400);
    },
    clearSearch() {
      this.filters.search = "";
      this.pagination.page = 1;
      this.fetchLogs();
    },
    clearAllFilters() {
      this.filters.search = "";
      this.filters.type_rda = "";
      this.filters.date_start = "";
      this.filters.date_end = "";
      this.pagination.page = 1;
      this.fetchLogs();
    },
    getAttemptsBadgeClass(attempts) {
      if (attempts <= 3) {
        return "badge-attempts-ok";
      } else if (attempts <= 5) {
        return "badge-attempts-warning";
      } else {
        return "badge-attempts-danger";
      }
    },
    switchTab(tab) {
      this.activeTab = tab;
      this.pagination.page = 1;
      this.fetchLogs();
    },
    changePage(page) {
      this.pagination.page = page;
      this.fetchLogs();
    },
    nextPage() {
      if (this.pagination.page < this.total_pages) {
        this.pagination.page += 1;
        this.fetchLogs();
      }
    },
    prevPage() {
      if (this.pagination.page > 1) {
        this.pagination.page -= 1;
        this.fetchLogs();
      }
    },
    async showDetails(log) {
      this.selectedLog = log;
      this.loadingDetails = true;
      try {
        const response = await ihceService.getLogDetails(log.id);
        this.logDetails = response.data.detalles;
      } catch (e) {
        console.error(e);
        Toast.fire({
          icon: "error",
          title: "Error al cargar los detalles del log."
        });
      } finally {
        this.loadingDetails = false;
      }
    },
    closeModal() {
      this.selectedLog = null;
      this.logDetails = [];
    },
    async retryLog(log) {
      this.retryingId = log.id;
      try {
        this.LoaderSpinnerShow();
        const response = await ihceService.retryLog(log.id);
        this.LoaderSpinnerHide();

        if (response.data.success) {
          Toast.fire({
            icon: "success",
            title: "Reintento de envío exitoso."
          });
          this.fetchLogs();
        } else {
          Toast.fire({
            icon: "error",
            title: "El reintento volvió a fallar: " + (response.data.message || "")
          });
          this.fetchLogs();
        }
      } catch (e) {
        console.error(e);
        this.LoaderSpinnerHide();
        Toast.fire({
          icon: "error",
          title: "Error al procesar el reintento."
        });
        this.fetchLogs();
      } finally {
        this.retryingId = null;
      }
    },
    async retryLogFromModal() {
      if (this.selectedLog) {
        const log = this.selectedLog;
        this.closeModal();
        await this.retryLog(log);
      }
    },
    formatDate(date) {
      return dayjs(date).format("DD/MM/YYYY hh:mm A");
    },
    formatPacienteName(u) {
      return trim(`${u.nombre_1 || ''} ${u.nombre_2 || ''} ${u.apellido_1 || ''} ${u.apellido_2 || ''}`);
    },
    formatJSON(obj) {
      if (typeof obj === 'string') {
        try {
          return JSON.stringify(JSON.parse(obj), null, 2);
        } catch(e) {
          return obj;
        }
      }
      return JSON.stringify(obj, null, 2);
    }
  },
  created() {
    this.fetchLogs();
  }
};

function trim(str) {
  return str.replace(/^\s+|\s+$/g, '').replace(/\s+/g, ' ');
}
</script>

<style scoped>
.text-wrap {
  white-space: pre-wrap !important;
  word-wrap: break-word !important;
}
.response-container pre {
  max-height: 250px;
  overflow-y: auto;
}


/* Custom Styles for IHCE Logs */
.badge-attempts-ok {
  background-color: #e0f2fe !important;
  color: #0369a1 !important;
}
.badge-attempts-warning {
  background-color: #fef3c7 !important;
  color: #d97706 !important;
}
.badge-attempts-danger {
  background-color: #fee2e2 !important;
  color: #dc2626 !important;
}

.badge-light-blue {
  background-color: #e2e8f0;
  color: #475569;
  border: 1px solid #cbd5e1;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
}
.animate-pulse {
  animation: pulse 1.8s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}
</style>
